import { init, jwtDelete, jwtDeleteHard, jwtDeleteSuccess, jwtSet, jwtSetSuccess } from './jwt.actions';
import { NGRX_JWT_STATE_CONFIG, NgrxJwtStateConfigInterface } from './jwt.injection-token';
import { inject } from '@angular/core';
import { Actions, ROOT_EFFECTS_INIT, createEffect, ofType } from '@ngrx/effects';
import { JwtTokens } from '@wizbii-utils/angular/jwt';
import { CookieService } from 'ngx-cookie-service';
import { map, tap } from 'rxjs';

interface CookieConfig {
  name: string;
  value: string;
  expires: number | Date;
  path: string;
  cookieDomain: string;
}

const readTokens = (cookieService: CookieService, jwtStateConfig: NgrxJwtStateConfigInterface): JwtTokens | null => {
  const rawTokens = JSON.parse(cookieService.get(jwtStateConfig.jwtCookieName) || 'null');
  return rawTokens ? rawTokens : null;
};

const setCookie = (cookieConfig: CookieConfig, cookieService: CookieService): void => {
  cookieService.set(
    cookieConfig.name,
    cookieConfig.value,
    cookieConfig.expires,
    cookieConfig.path,
    cookieConfig.cookieDomain === 'localhost' ? cookieConfig.cookieDomain : `.${cookieConfig.cookieDomain}`,
    cookieConfig.cookieDomain !== 'localhost',
    cookieConfig.cookieDomain === 'localhost' ? 'Lax' : 'None'
  );
};

const writeTokens = (
  tokens: JwtTokens,
  cookieService: CookieService,
  jwtStateConfig: NgrxJwtStateConfigInterface
): void => {
  const expiryExists = cookieService.check(jwtStateConfig.expiryCookieName);
  const msIn390Days = 1000 * 3600 * 24 * 390;
  const expiry = expiryExists
    ? new Date(parseInt(cookieService.get(jwtStateConfig.expiryCookieName), 10))
    : new Date(Date.now() + msIn390Days);

  if (!expiryExists) {
    setCookie(
      {
        name: jwtStateConfig.expiryCookieName,
        value: expiry.getTime().toString(),
        expires: expiry,
        path: '/',
        cookieDomain: jwtStateConfig.appEnvFqdn,
      },
      cookieService
    );
  }

  setCookie(
    {
      name: jwtStateConfig.jwtCookieName,
      value: JSON.stringify(tokens),
      expires: expiry,
      path: '/',
      cookieDomain: jwtStateConfig.appEnvFqdn,
    },
    cookieService
  );
};

const forgetTokens = (cookieService: CookieService, jwtStateConfig: NgrxJwtStateConfigInterface): void => {
  const forgetDate = new Date('Thu, 01 Jan 1970 00:00:01 GMT');

  setCookie(
    {
      name: jwtStateConfig.jwtCookieName,
      value: '',
      expires: forgetDate,
      path: '/',
      cookieDomain: jwtStateConfig.appEnvFqdn,
    },
    cookieService
  );

  setCookie(
    {
      name: jwtStateConfig.expiryCookieName,
      value: '',
      expires: forgetDate,
      path: '/',
      cookieDomain: jwtStateConfig.appEnvFqdn,
    },
    cookieService
  );
};

export const loadJwt = createEffect(
  (actions$ = inject(Actions), cookieService = inject(CookieService), jwtStateConfig = inject(NGRX_JWT_STATE_CONFIG)) =>
    actions$.pipe(
      ofType(ROOT_EFFECTS_INIT),
      map(() => readTokens(cookieService, jwtStateConfig)),
      map((jwtTokens) => init({ jwtTokens }))
    ),
  { functional: true }
);

export const jwtSetEffect = createEffect(
  (actions$ = inject(Actions), cookieService = inject(CookieService), jwtStateConfig = inject(NGRX_JWT_STATE_CONFIG)) =>
    actions$.pipe(
      ofType(jwtSet),
      tap(({ jwtTokens }) => writeTokens(jwtTokens, cookieService, jwtStateConfig)),
      map(({ jwtTokens }) => jwtSetSuccess({ jwtTokens }))
    ),
  { functional: true }
);

export const jwtDeleteEffect = createEffect(
  (actions$ = inject(Actions), cookieService = inject(CookieService), jwtStateConfig = inject(NGRX_JWT_STATE_CONFIG)) =>
    actions$.pipe(
      ofType(jwtDelete),
      tap(() => forgetTokens(cookieService, jwtStateConfig)),
      map(() => jwtDeleteSuccess())
    ),
  { functional: true }
);

export const jwtDeleteHardEffect = createEffect(
  (actions$ = inject(Actions), cookieService = inject(CookieService), jwtStateConfig = inject(NGRX_JWT_STATE_CONFIG)) =>
    actions$.pipe(
      ofType(jwtDeleteHard),
      tap(() => forgetTokens(cookieService, jwtStateConfig))
    ),
  { functional: true, dispatch: false }
);
